import SeoInfo from "@common/components/SeoInfo";
import getCanonicalUrl from "@common/helpers/getCanonicalUrl";
import SeoInfoProps from "@common/types/SeoInfoProps";
import CoveragePurchaseLayout from "@layouts/purchase/CoveragePurchaseLayout";
import getCmsPageData from "@modules/cms/api/getCmsPageData";
import CmsLayouts from "@modules/cms/components/CmsLayouts";
import { CmsLayout } from "@modules/cms/types/CmsLayout";
import { prefetchFeatureFlagList } from "@modules/feature-flags/hooks/useFeatureFlags";
import { GetStaticPropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { ReactElement, useEffect } from "react";
import StripeProvider from "@common/components/stripe/StripeProvider";
import { getServerSideQueryClient } from "@modules/api/queryClients";
import { dehydrate } from "@tanstack/react-query";
import { tryGetActiveAftermarketPurchase } from "@modules/purchase/types/Purchase";
import useAftermarketPurchase from "@modules/purchase/hooks/useAftermarketPurchase";
import useConvertExperience from "@modules/core/convert/hooks/useConvertExperience";
import { warrantyDiscountExperience } from "@modules/core/convert/types/ConvertExperienceType";
import { AftermarketDiscountConvertData } from "@modules/purchase/types/AftermarketDiscountConvertData";
import { WARRANTY_DISCOUNT_CAMPAIGN } from "@common/constants";
import Snackbar from "@common/components/Snackbar";
import { Trans, useTranslation } from "react-i18next";
import { formatCurrency } from "@common/helpers/numberFormatting";

type Props = {
  layouts: CmsLayout[];
  seoInfo: SeoInfoProps | null;
};

const CoveragePurchaseIndexPage = ({ layouts, seoInfo }: Props) => {
  const router = useRouter();
  const { t } = useTranslation(["purchase"]);

  const { purchase: purchaseResponse } = useAftermarketPurchase();
  const purchase = tryGetActiveAftermarketPurchase(purchaseResponse);

  const convertResult = useConvertExperience<AftermarketDiscountConvertData>(
    warrantyDiscountExperience
  );

  const { utm_campaign: utmCampaign } = router.query;
  const isPageLoadedFromDiscountCampaignEmail =
    (utmCampaign as string) === WARRANTY_DISCOUNT_CAMPAIGN;

  useEffect(() => {
    if (isPageLoadedFromDiscountCampaignEmail && purchase?.pricing.discount) {
      Snackbar.Pop({
        message: (
          <Trans
            t={t}
            i18nKey="purchase:our_discount_was_applied_to_quote"
            values={{
              discountValue: formatCurrency(
                purchase?.pricing.discount || 0,
                router?.locale || "en",
                { showCents: "never" }
              ),
            }}
            components={{
              bold: <span className="font-semibold text-primary-bright" />,
            }}
          />
        ),
      });
    }
  }, [
    isPageLoadedFromDiscountCampaignEmail,
    purchase?.pricing.discount,
    router?.locale,
    t,
  ]);

  return (
    <CoveragePurchaseLayout
      purchase={purchase}
      removePadding
      isOnIndex
      isOnLanding
      autoRedirectToPurchaseAfterLogin={false}
    >
      {seoInfo && <SeoInfo seoInfo={seoInfo} />}
      <CmsLayouts
        locale={router.locale}
        layouts={layouts ?? []}
        convertResult={convertResult}
      />
    </CoveragePurchaseLayout>
  );
};

CoveragePurchaseIndexPage.pageName = "CoveragePurchaseIndexPage";
CoveragePurchaseIndexPage.pageWrapper = (page: ReactElement) => (
  <StripeProvider pageIncludesPaymentForm={false}>{page}</StripeProvider>
);

export async function getStaticProps({
  locale,
  preview,
}: GetStaticPropsContext) {
  const queryClient = getServerSideQueryClient();
  await prefetchFeatureFlagList(queryClient);

  const { layouts, seoInfo } = await getCmsPageData(
    "aftermarket-landing",
    preview,
    locale?.toUpperCase()
  );

  const kijijiWarranty = "purchase/coverage?source=kijiji";
  if (seoInfo && !seoInfo.canonical) {
    seoInfo.canonical = getCanonicalUrl(kijijiWarranty);
  }

  return {
    props: {
      ...(await serverSideTranslations(locale || "en", ["common", "purchase"])),
      layouts,
      seoInfo,
      dehydratedState: dehydrate(queryClient),
    },
  };
}

export default CoveragePurchaseIndexPage;
